import { Fragment, useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { ActionButton } from '../../../../components/buttons/ActionButton';
import Badge from '../../../../components/badges/Badge';
import { formatVACAeroDashboardDateTime, formatVACAeroDashboardDuration } from '../../../../services/dashboard/vacaero-dashboard-service';
import { VACAeroDashboardNoData } from './VACAeroDashboardBlocks';

const PAGE_SIZE = 15;

export const VACAeroDashboardProcessesTable = ({ processes, page, onPageChange }) => {
  const [expandedId, setExpandedId] = useState(null);

  const totalPages = Math.max(1, Math.ceil(processes.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageProcesses = processes.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    setExpandedId(null);
  }, [page, processes]);

  const toggleProcess = (processId) => {
    setExpandedId(expandedId === processId ? null : processId);
  };

  const handlePageChange = (nextPage) => {
    setExpandedId(null);
    onPageChange(nextPage);
  };

  return (
    <div className="overflow-hidden rounded-md border border-perception-black-500">
      <div className="max-h-[320px] overflow-auto">
        <table className="w-full min-w-[900px]">
          <thead className="sticky top-0 z-[1] bg-perception-black-700">
          <tr className="border-b border-perception-black-500">
            <th className="w-10 px-3 py-3" />

            {['ID', 'Début', 'Fin', 'Temps de cycle', 'Alertes', 'Statut'].map((column) => (
              <th key={column} className="px-4 py-3 text-left text-xs font-semibold uppercase text-perception-gray-500">
                {column}
              </th>
            ))}
          </tr>
          </thead>

          <tbody>
          {pageProcesses.map((process) => {
            const isExpanded = expandedId === process.id;
            const hasAlerts = process.alerts.length > 0;

            return (
              <Fragment key={process.id}>
                <tr
                  className="cursor-pointer border-b border-perception-black-500 bg-perception-black-900 hover:bg-perception-black-700"
                  onClick={() => toggleProcess(process.id)}
                >
                  <td className="px-3 py-3 text-perception-gray-600">
                    {isExpanded ? <ChevronDownIcon className="size-4" /> : <ChevronRightIcon className="size-4" />}
                  </td>

                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-perception-gray-300">{process.id}</td>

                  <td className="whitespace-nowrap px-4 py-3 text-sm text-perception-gray-300">
                    {formatVACAeroDashboardDateTime(process.process_start)}
                  </td>

                  <td className="whitespace-nowrap px-4 py-3 text-sm text-perception-gray-300">
                    {formatVACAeroDashboardDateTime(process.process_end)}
                  </td>

                  <td className="whitespace-nowrap px-4 py-3 text-sm text-perception-gray-300">
                    {formatVACAeroDashboardDuration(process.process_duration)}
                  </td>

                  <td className="px-4 py-3 text-sm text-perception-gray-300">{process.alerts.length}</td>

                  <td className="px-4 py-3 text-sm">
                    <Badge text={process.status} type={hasAlerts ? 'yellow' : 'green'} />
                  </td>
                </tr>

                {isExpanded && (
                  <tr className="border-b border-perception-black-500 bg-perception-black-800">
                    <td colSpan={7} className="p-4">
                      <div className="flex flex-col gap-3">
                        <div className="text-xs font-semibold uppercase text-perception-gray-600">
                          Détail des alertes
                        </div>

                        {!hasAlerts ? (
                          <div className="text-sm text-perception-gray-500">Aucune alerte</div>
                        ) : (
                          <div className="flex flex-col divide-y divide-perception-black-500">
                            {process.alerts.map((alert) => (
                              <div key={alert.id} className="flex items-center gap-3 py-3">
                                <Badge text={formatVACAeroDashboardDateTime(alert.timestamp)} type="gray" />
                                <div className="text-sm text-perception-gray-300">{alert.label}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}

          {pageProcesses.length === 0 && (
            <tr>
              <td colSpan={7}>
                <VACAeroDashboardNoData />
              </td>
            </tr>
          )}
          </tbody>
        </table>
      </div>

      {processes.length > 0 && (
        <div className="flex items-center justify-end gap-3 border-t border-perception-black-500 bg-perception-black-700 px-4 py-3">
          <ActionButton
            text="Précédent"
            color="secondary"
            size="sm"
            disabled={safePage <= 1}
            onClick={() => handlePageChange(safePage - 1)}
          />

          <div className="text-xs uppercase text-perception-gray-600">
            Page {safePage} / {totalPages}
          </div>

          <ActionButton
            text="Suivant"
            color="secondary"
            size="sm"
            disabled={safePage >= totalPages}
            onClick={() => handlePageChange(safePage + 1)}
          />
        </div>
      )}
    </div>
  );
};